import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { GridValidRowModel } from "@mui/x-data-grid";
import { ThemeProvider, createTheme, type SxProps, type Theme } from "@mui/material/styles";
import { Tooltip } from "@mui/material";
import { Context } from "./context";
import { bindActionCreators } from "../actions/actionCreators";
import * as userActions from "../actions/userActions";

interface FreeSingleSelectProps<T extends GridValidRowModel = any> {
  onValueSelected: (value: T) => void;
  options: T[];
  value?: T;
  placeholder?: string;
  disabled?: boolean;
  formatValues?: (values: T[]) => string[];
  formatInputs?: (values: (object | string)[]) => T[];
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  disableChipDeletion?: boolean;
  disableFreeSolo?: boolean;
  error?: boolean;
  hideTooltip?: boolean;
  tooltipTitle?: string;
  onDeleteOption?: (option: T) => void;
  deletableOptions?: T[];
  maxVisibleItems?: number;
  useEmailSuggestions?: boolean;
  useCategorySuggestions?: boolean;
  enableDelete?: boolean;
  enableUsageSort?: boolean;
  preserveOptionOrder?: boolean;
  pinnedEmails?: string[];
  dropdownMinWidth?: number;
  matchInputWidth?: boolean;
  sx?: SxProps<Theme>;
}

// Factory to create a Popper component that can access width preferences via closure
const createCustomPopper = (minWidth?: number, matchInputWidth?: boolean) => {
  return function CustomPopper(props) {
    const { anchorEl } = props;
    const anchorWidth =
      (anchorEl && (anchorEl as HTMLElement).getBoundingClientRect?.().width) ||
      undefined;
    const computedMinWidth = typeof minWidth === "number" ? minWidth : 300;
    const useAnchorWidth = !!matchInputWidth;
    return (
      <Popper
        {...props}
        style={{
          width: useAnchorWidth ? anchorWidth : undefined,
          minWidth: useAnchorWidth ? anchorWidth : computedMinWidth,
        }}
        placement="bottom-start"
      />
    );
  };
};

const finalTheme = createTheme({
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #d2d2d2",
          padding: "1.5px 14px",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:hover": { borderColor: "#242C63" },
          "&:focus-within": { borderColor: "#242C63", outline: "none" },
          "&:has(.MuiAutocomplete-inputRoot.Mui-disabled), &:has(.MuiAutocomplete-inputRoot.Mui-disabled):hover":
            {
            backgroundColor: "#f5f5f5",
            borderColor: "#bdbdbd",
            pointerEvents: "none",
          },
          "& .MuiAutocomplete-input.Mui-disabled, & .MuiInputBase-input.Mui-disabled":
            {
              WebkitTextFillColor: "#B0B0B0",
              color: "#B0B0B0",
              backgroundColor: "transparent",
            },
          "& .Mui-disabled .MuiSvgIcon-root, & .MuiSvgIcon-root.Mui-disabled": {
            color: "#B0B0B0",
          },
        },
        inputRoot: {
          padding: "6px 0px 6px 0px !important",
        },
        input: {
          padding: "0 !important",
        },
      },
    },
  },
});

function formatOption(option: any, props: FreeSingleSelectProps<any>): string {
  if (option) {
    if (props.formatValues) {
      const formatted = props.formatValues([option]);
      if (formatted?.length) {
        return formatted[0];
      }
    } else {
      return option.toString();
    }
  }
  return "";
}

export const FreeSingleSelect: React.FC<FreeSingleSelectProps> = (
  props: FreeSingleSelectProps,
): React.ReactElement => {
  const appContext = React.useContext(Context);
  const actions = React.useMemo(
    () => ({
      saveInvoker: userActions.save,
      refreshInvoker: () => userActions.getInvoker()(appContext.dispatch),
    }),
    [appContext.dispatch],
  );

  // Compute listbox max height based on desired visible items
  const itemHeight = 36; // approximate MUI option height
  const maxItems =
    props.maxVisibleItems && props.maxVisibleItems > 0
      ? props.maxVisibleItems
      : undefined;
  const listboxStyle: React.CSSProperties | undefined = maxItems
    ? { maxHeight: maxItems * itemHeight, overflowY: "auto" }
    : undefined;

  // Usage-based sorting using counts derived from suggestion arrays
  const sortByUsage = (
    options: string[],
    type: "email" | "category",
    counts: Record<string, number>,
  ): string[] => {
    if (props.enableUsageSort === false) return options;
    return [...options].sort((a, b) => {
      const cb = counts[b] || 0;
      const ca = counts[a] || 0;
      if (cb !== ca) return cb - ca;
      return a.localeCompare(b);
    });
  };

  // Compute suggestion type and merge options
  const useEmail = !!props.useEmailSuggestions;
  const useCategory = !!props.useCategorySuggestions;
  const invokerEmailSuggestions =
    appContext.state.invoker?.emailSuggestions || [];
  const invokerCategorySuggestions =
    appContext.state.invoker?.categorySuggestions || [];
  // Build usage maps from suggestion objects
  const emailUsage: Record<string, number> = invokerEmailSuggestions.reduce(
    (acc, e: any) => {
      const v = (e?.value ?? e)?.toString();
      if (v) acc[v] = e?.count ?? 0;
      return acc;
    },
    {} as Record<string, number>,
  );
  const categoryUsage: Record<string, number> =
    invokerCategorySuggestions.reduce(
      (acc, e: any) => {
        const v = (e?.value ?? e)?.toString();
        if (v) acc[v] = e?.count ?? 0;
        return acc;
      },
      {} as Record<string, number>,
    );

  let mergedOptions: string[] = Array.from(
    new Set((props.options || []).map((o) => o?.toString?.() ?? o)),
  );
  if (useEmail) {
    invokerEmailSuggestions.forEach((e: any) =>
      mergedOptions.push((e?.value ?? e)?.toString()),
    );
    mergedOptions = Array.from(new Set(mergedOptions));
    if (!props.preserveOptionOrder) {
      const pinnedEmails = (props.pinnedEmails || []).filter(Boolean);
      const invokerEmail = appContext.state.invoker?.email;
      pinnedEmails.forEach((e) => mergedOptions.push(e));
      if (invokerEmail) mergedOptions.push(invokerEmail);
      mergedOptions = Array.from(new Set(mergedOptions));
      mergedOptions = sortByUsage(mergedOptions, "email", emailUsage);
      const pinned: string[] = [];
      if (invokerEmail) pinned.push(invokerEmail);
      pinnedEmails.forEach((e) => {
        if (!pinned.includes(e)) pinned.push(e);
      });
      const pinnedSet = new Set(pinned);
      mergedOptions = [
        ...pinned,
        ...mergedOptions.filter((o) => !pinnedSet.has(o)),
      ];
    }
  }
  if (useCategory) {
    invokerCategorySuggestions.forEach((e: any) =>
      mergedOptions.push((e?.value ?? e)?.toString()),
    );
    mergedOptions = Array.from(new Set(mergedOptions));
    if (!props.preserveOptionOrder) {
      mergedOptions = sortByUsage(mergedOptions, "category", categoryUsage);
    }
  }

  // Deletable options come from invoker suggestions for the active type
  const showDelete = props.enableDelete !== false;
  const deletableSet = new Set<string>([
    ...(useEmail
      ? invokerEmailSuggestions.map((e: any) => (e?.value ?? e)?.toString())
      : []),
    ...(useCategory
      ? invokerCategorySuggestions.map((e: any) => (e?.value ?? e)?.toString())
      : []),
  ]);

  const errorSx = props.error
    ? {
        borderColor: "#d32f2f",
        "&:hover": { borderColor: "#d32f2f" },
        "&:focus-within": { borderColor: "#d32f2f" },
      }
    : undefined;

  return (
    <ThemeProvider theme={finalTheme}>
      <Autocomplete
        sx={[props.sx, errorSx].filter(Boolean) as SxProps<Theme>}
        disableClearable
        slots={{
          popper: createCustomPopper(
            props.dropdownMinWidth,
            props.matchInputWidth,
          ),
        }}
        selectOnFocus
        fullWidth
        freeSolo={!props.disableFreeSolo}
        options={mergedOptions}
        value={props.value || ""}
        slotProps={{
          listbox: listboxStyle ? { style: listboxStyle } : undefined,
        }}
        renderOption={(renderProps, option, { selected }) => {
          const isDeletable = showDelete && deletableSet.has(option as string);
          const label = formatOption(option, props);
          return (
            <li
              {...renderProps}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {label}
              </span>
              {isDeletable && (
                <IconButton
                  size="small"
                  aria-label="delete suggestion"
                  onClick={(e) => {
                    e.stopPropagation();
                    const val = option as string;
                    if (useEmail) {
                      const updated = (
                        appContext.state.invoker?.emailSuggestions || []
                      ).filter((c: any) => (c?.value ?? c)?.toString() !== val);
                      const updatedInvoker = {
                        ...appContext.state.invoker,
                        emailSuggestions: updated,
                      };
                      actions
                        .saveInvoker(updatedInvoker)
                        .then(() => actions.refreshInvoker());
                    } else if (useCategory) {
                      const updated = (
                        appContext.state.invoker?.categorySuggestions || []
                      ).filter((c: any) => (c?.value ?? c)?.toString() !== val);
                      const updatedInvoker = {
                        ...appContext.state.invoker,
                        categorySuggestions: updated,
                      };
                      actions
                        .saveInvoker(updatedInvoker)
                        .then(() => actions.refreshInvoker());
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </li>
          );
        }}
        getOptionLabel={(option) => {
          return formatOption(option, props);
        }}
        renderInput={(inputParams) => {
          const legacyParams = inputParams as unknown as {
            InputProps?: Record<string, any>;
            inputProps?: Record<string, any>;
          };
          const inputProps =
            legacyParams.InputProps ?? inputParams.slotProps.input;
          const htmlInputProps =
            legacyParams.inputProps ?? inputParams.slotProps.htmlInput;
          const textField = (
            <TextField
              {...inputParams}
              variant="standard"
              placeholder={props.value || props.placeholder || ""}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              onBlur={(_event) => {
                if (!props.disableFreeSolo) {
                  const rawVal = (htmlInputProps as { value?: string })?.value;
                  props.onValueSelected(rawVal as any);
                }
              }}
              slotProps={{
                input: {
                  ...inputProps,
                  disableUnderline: true,
                },
                htmlInput: {
                  ...htmlInputProps,
                  autoComplete: "new-password",
                },
              }}
            />
          );

          return props.hideTooltip ? (
            textField
          ) : (
            <Tooltip title={props.tooltipTitle}>{textField}</Tooltip>
          );
        }}
        onChange={(event, value, _reason) => {
          event?.stopPropagation();
          const val = (
            props.formatInputs ? props.formatInputs([value])[0] : value
          ) as string;
          props.onValueSelected(val as any);
        }}
        isOptionEqualToValue={
          props.isOptionEqualToValue || ((option, value) => option === value)
        }
        disabled={props.disabled}
      />
    </ThemeProvider>
  );
};
